import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import BloqLogo from "@/components/BloqLogo";
import { useAuth, formatApiError } from "@/contexts/AuthContext";
import { BETA_ACCEPT_KEY } from "@/pages/BetaDisclaimer";

function startGoogleOAuth() {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/app";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(
    redirectUrl
  )}`;
}

export default function Signup() {
  const { signup } = useAuth();
  const navigate = useNavigate();

  // Gate: anyone landing directly on /signup without having accepted the
  // beta disclaimer first is bounced through /beta-disclaimer. This is a
  // soft gate (sessionStorage) — the form itself also requires explicit
  // checkbox confirmation before letting the user submit.
  useEffect(() => {
    try {
      if (sessionStorage.getItem(BETA_ACCEPT_KEY) !== "1") {
        navigate("/beta-disclaimer", { replace: true });
      }
    } catch {
      navigate("/beta-disclaimer", { replace: true });
    }
  }, [navigate]);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [busy, setBusy] = useState(false);

  // Two required acceptance gates — beta disclaimer, and a combined
  // Terms-of-Service + Privacy-Policy box (user asked to merge the two).
  const [agreeBeta, setAgreeBeta] = useState(false);
  const [agreeLegal, setAgreeLegal] = useState(false);
  const allAgreed = agreeBeta && agreeLegal;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!allAgreed) {
      toast.error("Please accept the beta disclaimer and Terms/Privacy before continuing");
      return;
    }
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (password !== password2) {
      toast.error("Passwords don't match — re-enter to confirm");
      return;
    }
    if (!firstName.trim()) {
      toast.error("First name is required");
      return;
    }
    setBusy(true);
    try {
      await signup({ first_name: firstName.trim(), last_name: lastName.trim() }, email.trim(), password);
      toast.success("Welcome to Bloq — check your inbox to verify your email");
      navigate("/app/settings?tab=profile&newuser=1");
    } catch (err) {
      toast.error(formatApiError(err) || "Could not create your account");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <div className="bloq-container pt-7">
        <BloqLogo size={26} />
      </div>
      <div className="flex-1 grid lg:grid-cols-2 items-center bloq-container py-12 gap-16">
        <div className="max-w-md mx-auto w-full" data-testid="signup-card">
          <p className="bloq-eyebrow mb-3">Get started</p>
          <h1 className="text-4xl sm:text-5xl bloq-headline mb-3">
            A meeting workflow that <span className="bloq-gradient-text">finishes for you.</span>
          </h1>
          <p className="text-[#4a4a52] text-base leading-relaxed mb-10">
            Set up your workspace in 60 seconds.
          </p>

          <button
            data-testid="signup-google-button"
            onClick={startGoogleOAuth}
            disabled={!allAgreed}
            className={`w-full flex items-center justify-center gap-3 px-4 py-3 bg-white shadow-sm hover:shadow-md transition-all mb-4 font-medium ${
              allAgreed ? "" : "opacity-40 cursor-not-allowed"
            }`}
            title={allAgreed ? "" : "Accept the beta disclaimer and Terms/Privacy below first"}
          >
            <svg className="w-5 h-5" viewBox="0 0 48 48">
              <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.6 32.6 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z" />
              <path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.7 16.2 19 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
              <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.3 26.7 36 24 36c-5.2 0-9.6-3.4-11.2-8l-6.5 5C9.5 39.6 16.2 44 24 44z" />
              <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.6l6.2 5.2c-.4.4 6.5-4.7 6.5-14.8 0-1.2-.1-2.3-.4-3.5z" />
            </svg>
            Continue with Google
          </button>

          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px bg-black/8" />
            <p className="text-xs uppercase tracking-widest text-[#82828c]">or with email</p>
            <div className="flex-1 h-px bg-black/8" />
          </div>

          <form onSubmit={onSubmit} className="flex flex-col gap-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs uppercase tracking-widest text-[#82828c] font-medium">First name</label>
                <input required value={firstName} onChange={(e) => setFirstName(e.target.value)}
                  className="bloq-input mt-1" data-testid="signup-firstname-input" placeholder="Alex" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-[#82828c] font-medium">Last name</label>
                <input value={lastName} onChange={(e) => setLastName(e.target.value)}
                  className="bloq-input mt-1" data-testid="signup-lastname-input" placeholder="Rivera" />
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-[#82828c] font-medium">
                Email
              </label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bloq-input mt-1"
                data-testid="signup-email-input"
                placeholder="you@company.com"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-[#82828c] font-medium">
                Password
              </label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bloq-input mt-1"
                data-testid="signup-password-input"
                placeholder="At least 6 characters"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-[#82828c] font-medium">
                Confirm password
              </label>
              <input
                type="password"
                required
                value={password2}
                onChange={(e) => setPassword2(e.target.value)}
                className={`bloq-input mt-1 ${password2 && password !== password2 ? "border-[#c43838]" : ""}`}
                data-testid="signup-password-confirm-input"
                placeholder="Re-enter to confirm"
              />
              {password2 && password !== password2 && (
                <p className="text-xs text-[#c43838] mt-1" data-testid="signup-password-mismatch">
                  Passwords don&apos;t match yet
                </p>
              )}
            </div>

            {/* Required acceptance checkboxes. Both must be ticked. */}
            <div
              className="mt-3 flex flex-col gap-2 bloq-card p-4 bg-[#fbfcfc]"
              data-testid="signup-agreements"
            >
              <label className="flex items-start gap-2.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={agreeBeta}
                  onChange={(e) => setAgreeBeta(e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-[#005e7a]"
                  required
                  data-testid="signup-agree-beta-checkbox"
                />
                <span className="text-xs text-[#1a1a1f] leading-relaxed">
                  I've read and accept the{" "}
                  <Link to="/beta-disclaimer" className="text-[#005e7a] underline">
                    beta disclaimer
                  </Link>{" "}
                  — Bloq is unfinished, may break, and is provided as-is.
                </span>
              </label>
              <label className="flex items-start gap-2.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={agreeLegal}
                  onChange={(e) => setAgreeLegal(e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-[#005e7a]"
                  required
                  data-testid="signup-agree-legal-checkbox"
                />
                <span className="text-xs text-[#1a1a1f] leading-relaxed">
                  I've read and agree to Bloq's{" "}
                  <Link to="/terms" className="text-[#005e7a] underline">
                    Terms of Service
                  </Link>{" "}
                  and{" "}
                  <Link to="/privacy" className="text-[#005e7a] underline">
                    Privacy Policy
                  </Link>
                  .
                </span>
              </label>
            </div>

            <button
              type="submit"
              data-testid="signup-submit-button"
              className={`mt-3 flex items-center justify-center gap-2 px-6 py-3.5 font-medium transition-opacity ${
                allAgreed
                  ? "bloq-btn-primary"
                  : "bg-[#4a4a52]/20 text-[#4a4a52] cursor-not-allowed"
              }`}
              disabled={busy || !allAgreed}
              title={allAgreed ? "" : "Accept both above to continue"}
            >
              {busy ? "Creating your workspace…" : (<>Create account <ArrowRight className="w-4 h-4" /></>)}
            </button>
          </form>

          <p className="mt-7 text-sm text-[#4a4a52]">
            Already on Bloq?{" "}
            <Link to="/login" className="font-medium text-[#005e7a]" data-testid="signup-to-login-link">
              Log in
            </Link>
          </p>
        </div>

        <div className="hidden lg:block">
          <div className="bloq-glass-strong p-8 relative overflow-hidden">
            <div className="bloq-hero-orb" style={{ width: 400, height: 400, top: -100, right: -120 }} />
            <p className="bloq-eyebrow">What you'll unlock</p>
            <h3 className="bloq-headline text-2xl mt-2 mb-6 max-w-sm">
              Every meeting becomes the start of something — not the end of it.
            </h3>
            <ul className="flex flex-col gap-4 text-[#4a4a52]">
              {[
                "Default workspace and roadmap board ready in seconds",
                "Public booking link auto-generated on signup",
                "AI summaries and follow-up drafts on every call",
                "Beta access — your feedback shapes what ships next",
              ].map((t, i) => (
                <li key={i} className="flex items-start gap-3 text-sm">
                  <span className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0" style={{ background: "#005e7a" }} />
                  {t}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
